import { Injectable } from "@angular/core";
import * as moment from 'moment';
import { AmsNotifierService } from "src/app/shared/services/ams-notifier.service";
import { LanguageService } from "src/app/shared/services/language.service";
import { TimeConverter } from "src/app/shared/services/time-converter.service";

export interface ConvertResult {
  isValid: boolean;
  quickEntries?: any [];
}

@Injectable({
  providedIn: 'root'
})
export class GridToQuickEntryConverter {
  showEmployeeColumn: boolean;

  constructor(private languageService: LanguageService, private amsNotifier: AmsNotifierService, private timeConverter: TimeConverter) {}

  convert(gridRows: any[]): ConvertResult {
    const result: ConvertResult = { isValid: true, quickEntries: [] };

    gridRows.forEach((row: any) => {
      if (row.type === "insert") {
        result.quickEntries.push(this.convertRow(row));
      }
    });

    const isValid = this.validateRows(result);

    return isValid ? result : { isValid: false };
  }

  private convertRow(row: any): any {
    return {
      Date: row.data.Date ? moment(row.data.Date).format() : moment(row.key.Date).format(),
      EmployeeId: row.data.EmployeeId ? row.data.EmployeeId : row.key.EmployeeId,      
      CarrierId: row.data.CarrierId ? row.data.CarrierId : row.key.CarrierId,
      CarrierProjectId: row.data.CarrierProjectId ? row.data.CarrierProjectId : row.key.CarrierProjectId,
      Project: row.data.Project ? row.data.Project : row.key.Project,
      ProjectId: row.data.ProjectId ? row.data.ProjectId : row.key.ProjectId,
      EquipmentId1: row.data.EquipmentId1 ? row.data.EquipmentId1 : row.key.EquipmentId1,
      EquipmentId2: row.data.EquipmentId2 ? row.data.EquipmentId2 : row.key.EquipmentId2,
      TimesAggregatedByTimeType: this.getTimeTypeColumns(row)
    };
  }

  private getTimeTypeColumns(row: any): any[] {
    const timeTypeColumns = [];
    const columns = row.type === "insert"
      ? Object.keys(row.data).filter(key => key.startsWith('TimeType_'))
      : Object.keys(row.key).filter(key => key.startsWith('TimeType_'));

    for (const column of columns) {
      timeTypeColumns.push(this.getTimeTypeColumn(row, column));
    }

    return timeTypeColumns;
  }

  private getTimeTypeColumn(row: any, column: any): any {
    const value = row.data[column] ?? row.key[column];

    return {
      TimeTypeId: parseInt(column.split('_')[1], 10),
      Seconds: this.timeConverter.getTimeInputConvertedToSeconds(value)
    };
  }

  private validateRows(convertedResult: ConvertResult): boolean {
    for (const row of convertedResult.quickEntries) {
      if (!this.isValid(row)) { return false; }
    }

    return true;
  }

  isValid(row: any): boolean {
    if (!row.Date || !moment(row.Date).isValid()) {
      this.amsNotifier.errorWithForcedTimeout(this.languageService.LNG("Please.Select.Date"));
      return false;
    }

    if (this.showEmployeeColumn && (!row.EmployeeId || row.EmployeeId <= 0)) {
        this.amsNotifier.errorWithForcedTimeout(this.languageService.LNG("Please.Select.Employee"));
        return false;
      }
    
    if (!row.CarrierProjectId || row.CarrierProjectId <= 0) {
      this.amsNotifier.errorWithForcedTimeout(this.languageService.LNG("Please.Select.Project"));
      return false;
    }

    if (!this.hasAssignedAnyTimeType(row)) {
      this.amsNotifier.errorWithForcedTimeout(this.languageService.LNG("Label.Please.Type.Hours"));
      return false;
    }

    return true;
  }

  private hasAssignedAnyTimeType(row: any): boolean {
    return row.TimesAggregatedByTimeType?.filter((timeType: any) => timeType.Seconds > 0).length > 0;
  }
}
